// @ts-nocheck
// Every agent on a call right now, pulled straight from the live roster —
// this is the team lead's listen-in list. The customer on each call (number +
// saved contact name) comes from the live-calls feed, matched by agent id; it is
// only fetched while at least one agent is on a call.
import React, { useMemo } from 'react';
import { PhoneCall } from 'lucide-react';
import { Dot, EmptyNote, HAIRLINE, Panel, Pill, TONE, TableShell, Td, Th } from './teamUi';
import SnoopButtons from './SnoopButtons';
import { useLiveCalls } from '@/features/live-calls/useLiveCalls';

const LIVE_QUERY = { limit: 200 };

export default function TeamOnCallPanel({ agents, snoop, canSnoop, onOpenAgent }) {
  const onCall = agents.filter((a) => a.stateKey === 'onCall');

  // agent id -> live call (any leg the agent is on: own call, internal callee, transfer target).
  const feed = useLiveCalls(LIVE_QUERY, onCall.length > 0);
  const callByAgent = useMemo(() => {
    const map = new Map();
    (feed.data?.calls || []).forEach((c) => {
      [c.agent, c.calleeAgent, c.transfer?.toAgent].forEach((a) => {
        if (a?.id != null && !map.has(String(a.id))) map.set(String(a.id), c);
      });
    });
    return map;
  }, [feed.data]);

  const dash = <span className="text-[11px] text-muted-foreground">—</span>;
  const customerNameCell = (row) => {
    const call = callByAgent.get(String(row.id));
    if (!call) return dash;
    if (call.direction === 'INT') {
      return <span className="text-[11px] text-muted-foreground">Internal call</span>;
    }
    return call.customerName ? (
      <span className="truncate text-[12px] font-medium">{call.customerName}</span>
    ) : (
      dash
    );
  };
  const customerNumberCell = (row) => {
    const call = callByAgent.get(String(row.id));
    if (!call || call.direction === 'INT' || !call.customerNo) return dash;
    return <span className="text-[12px] tabular-nums">{call.customerNo}</span>;
  };

  return (
    <Panel className="p-3.5">
      {onCall.length > 0 ? (
        <div className="mb-3 flex items-center justify-between gap-2">
          <p className="text-[11px] text-muted-foreground">
            {onCall.length} agent{onCall.length === 1 ? '' : 's'} connected — monitor, whisper or barge in
          </p>
          {snoop.session ? (
            <button type="button" onClick={snoop.stop} disabled={snoop.busy}>
              <Pill tone="crit">{snoop.busy ? 'Stopping…' : `Stop ${snoop.session.mode}`}</Pill>
            </button>
          ) : null}
        </div>
      ) : null}

      {onCall.length === 0 ? (
        <EmptyNote>
          <span className="inline-flex items-center gap-1.5">
            <PhoneCall size={13} />
            No connected calls
          </span>
        </EmptyNote>
      ) : (
        <div className={`overflow-hidden rounded-xl border ${HAIRLINE}`}>
          <TableShell maxH={320}>
            <thead>
              <tr>
                <Th>Agent</Th>
                <Th>Customer Name</Th>
                <Th>Customer Number</Th>
                <Th>Call group</Th>
                <Th>Extension</Th>
                <Th>Activity</Th>
                <Th>Call state</Th>
                <Th align="center">Listen</Th>
              </tr>
            </thead>
            <tbody>
              {onCall.map((row) => (
                <tr key={row.id} className="transition-colors hover:bg-muted/40">
                  <Td>
                    <button
                      type="button"
                      onClick={() => onOpenAgent(row)}
                      className="flex items-center gap-2 text-left"
                    >
                      <Dot tone="call" size={6} />
                      <span className="truncate text-[12px] font-medium">{row.name}</span>
                    </button>
                  </Td>
                  <Td>{customerNameCell(row)}</Td>
                  <Td>{customerNumberCell(row)}</Td>
                  <Td className="text-[11px] text-muted-foreground">{row.groupName || '—'}</Td>
                  <Td className="text-[11px] tabular-nums text-muted-foreground">{row.extension || '—'}</Td>
                  <Td className="text-[11px] text-muted-foreground">{row.activity || '—'}</Td>
                  <Td>
                    <span className={`text-[11px] font-medium ${row.snoop.answered ? TONE.good.text : TONE.warn.text}`}>
                      {row.snoop.answered ? 'Answered' : 'Connecting'}
                    </span>
                  </Td>
                  <Td align="center">
                    <SnoopButtons row={row} snoop={snoop} allowed={canSnoop} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        </div>
      )}
    </Panel>
  );
}
